import { ManageIntegrationModel } from './ManageIntegrationModel';
import * as moment from 'moment';
import { environment } from '../../environments/environment';
import { AutomateIntegrationModel } from './AutomateIntegrationModel';

export enum AccountType {
  SYSTEM = 'SYSTEM',
  MSP = 'MSP',
  COMPANY = 'COMPANY'
}

export enum RoleType {
  ADMIN = 'Identify System Admin',
  USER = 'Identify User',
  CERTIFICATION_ADMIN = 'Identify Certification Admin',
  SUPPORT_ADMIN = 'Identify Support Admin'
}

export enum InstanceStatusType {
  active,
  locked,
  stopped,
  terminated
}

export const PreferenceCategories = [
  {category: 'ui', label: 'General'},
  {category: 'notifications', label: 'Notification Emails'},
  {category: 'marketing', label: 'Marketing'}
]

export const PreferenceDisplay = [
  {key: 'ui.showSplashScreen', category: 'ui', label: 'Display the Welcome Screen'},
  {key: 'ui.includeMasterInCompanies', category: 'ui', label: 'Include the Primary Account in Companies page listing'},

  {key: 'notifications.marketing.inApp', category: 'marketing', label: 'Show Marketing Messages In App'},
  {key: 'notifications.marketing.email', category: 'marketing', label: 'Receive Identify Marketing Emails'},

  {key: 'notifications.informational.email', category: 'notifications', label: 'Informational'},
  {key: 'notifications.systemAlert.email', category: 'notifications', label: 'System Alert'},
  {key: 'notifications.newAssessment.email', category: 'notifications', label: 'New Assessment'},
  {key: 'notifications.completedAssessment.email', category: 'notifications', label: 'Completed Assessment'},
]

export const AccountFeatureTypes = [
  {
    featureType: 'ALLOWED_ASSESSMENT_ID',
    description: 'What assessment is available to accounts with this plan?',
    dataType: 'array'
  },
  {
    featureType: 'ALLOW_SUBACCOUNTS',
    description: 'Do accounts with this plan have sub accounts?',
    dataType: 'boolean'
  },
  {
    featureType: 'ALLOW_CUSTOM_BRAND',
    description: 'Can accounts with this plan create a custom brand? Only applies to master accounts.',
    dataType: 'boolean'
  },
  {
    featureType: 'INCLUDE_CLIENT_ATTESTATION',
    description: 'Should the results report for sub accounts of an account with this plan include the attestation letter page?',
    dataType: 'boolean'
  },
  {
    featureType: 'ALLOW_REASSESSMENT',
    description: 'Can a new assessment be created from a previosly completed assessment?',
    dataType: 'boolean'
  },
  {
    featureType: 'SUB_ACCOUNT_PLAN',
    description: 'If this plan allows sub accounts, what plan should the sub accounts be created with?',
    dataType: 'array'
  },
  {
    featureType: 'NUM_SUBACCOUNTS',
    description: 'How many sub accounts are allowed for master accounts with this plan?',
    dataType: 'number'
  },
  {
    featureType: 'DASHBOARD_ENABLED',
    description: 'Do accounts with this plan have access to the full dashboard UI?',
    dataType: 'boolean'
  },
  {
    featureType: 'ANALYTICS_ENABLED',
    description: 'Do accounts with this plan have access to the analytics section?',
    dataType: 'boolean'
  },
  {
    featureType: 'INCLUDE_REPORT_ANALYTICS',
    description: 'Should the results report for this account or it\'s sub accounts include the analytics page?',
    dataType: 'boolean'
  },
  {
    featureType: 'ALLOW_SCANNER',
    description: 'Do accounts with this plan have access to the scanner?',
    dataType: 'boolean'
  },
  {
    featureType: 'ALLOW_CERTIFICATION',
    description: 'Do accounts with this plan have access to the certification module?',
    dataType: 'boolean'
  },
  {
    featureType: 'ALLOW_SPLASH_SCREEN',
    description: 'Do accounts with this plan have the welcome screen available?',
    dataType: 'boolean'
  },

]

export class BaseModel {
  public id?: number;

  constructor(id: number) {
    this.id = id;
  }

  compareFn(bm2: BaseModel) : boolean {
    return this && bm2 ? this.id === bm2.id : this === bm2;
  }
}


export class Account extends BaseModel {
  public name?: string;
  public type?: AccountType = AccountType.COMPANY;
  public planId?: number;
  public planSubscriptionId?: number;
  public planSubscription?: PlanSubscription;
  public masterPlanSubscription?: PlanSubscription;
  public parentId?: number;
  public referralCode?: string;
  public createdAt?: Date;
  public updatedAt?: Date;
  public summary?: AccountSummary;
  public isActive?: boolean;
  public integrationPartnerId?: number;
  public brandingId: number;
  public centralServicePartnerId?: string;
  public instanceId?: string;
  public instanceStatus?: string;
  public partner?: ManageIntegrationModel;
  public manageIntegration?: ManageIntegrationModel;
  public automateIntegration?: AutomateIntegrationModel;

  constructor(obj: any = {} as Account) {
    super(obj.id);
    this.name = obj.name;
    this.type = obj.type;
    this.planId = obj.planId;
    this.planSubscriptionId = obj.planSubscriptionId;
    if (obj.planSubscription) {
      this.planSubscription = new PlanSubscription(obj.planSubscription);
    }
    if (obj.masterPlanSubscription) {
      this.masterPlanSubscription = new PlanSubscription(obj.masterPlanSubscription);
    }
    this.parentId = obj.parentId;
    this.referralCode = obj.referralCode;
    this.createdAt = obj.createdAt;
    this.updatedAt = obj.updatedAt;
    this.summary = new AccountSummary(obj.summary);
    this.isActive = obj.isActive;
    this.integrationPartnerId = obj.integrationPartnerId;
    this.brandingId = obj.brandingId;
    this.centralServicePartnerId = obj.centralServicePartnerId;
    this.instanceId = obj.instanceId;
    this.instanceStatus = obj.instanceStatus;
    if (obj.partner){
      this.partner = new ManageIntegrationModel(obj.partner);
    }

    if (obj.manageIntegration) {
      this.manageIntegration = new ManageIntegrationModel(obj.manageIntegration);
    }

    if (obj.automateIntegration) {
      this.automateIntegration = new AutomateIntegrationModel(obj.automateIntegration);
    }
  }

  isInTrial(): boolean {
    if (this.type === AccountType.SYSTEM) {
      return false;
    } else {
      const planSub = (this.parentId === undefined || this.parentId === null) ? this.planSubscription : this.masterPlanSubscription;

      if (planSub && planSub.trialPeriodEndDate && planSub.paymentStatus === 'trial') {
        return moment(planSub.trialPeriodEndDate).isAfter(moment());
      } else {
        return false;
      }
    }
  }

}

export class AccountSummary {
  public totalAssessments?: number;
  public notStartedAssessments?: number;
  public inProgressAssessments?: number;
  public completedAssessments?: number;
  public recentAssessmentId?: number;
  public recentAssessmentTemplateId?: number;
  public recentAssessmentStatus?: string;
  public recentAssessmentCurrentPage?: number;
  public recentAssessmentPercentComplete?: number;
  public recentAssessmentPercentCompleteStr?: string;
  public recentAssessmentScore?: number;
  public recentAssessmentUpdatedAt?: Date;

  constructor(obj: any = {} as AccountSummary) {
    this.totalAssessments = obj.totalAssessments;
    this.notStartedAssessments = obj.notStartedAssessments;
    this.inProgressAssessments = obj.inProgressAssessments;
    this.completedAssessments = obj.completedAssessments;
    this.recentAssessmentId = obj.recentAssessmentId;
    this.recentAssessmentTemplateId = obj.recentAssessmentTemplateId;
    this.recentAssessmentStatus = obj.recentAssessmentStatus;
    this.recentAssessmentCurrentPage = obj.recentAssessmentCurrentPage;
    this.recentAssessmentPercentComplete = obj.recentAssessmentPercentComplete;
    this.recentAssessmentPercentCompleteStr = `${Math.floor(this.recentAssessmentPercentComplete * 100)}%`;
    this.recentAssessmentScore = obj.recentAssessmentScore;
    this.recentAssessmentUpdatedAt = obj.recentAssessmentUpdatedAt;
  }
}

export class AccountProfileItem {
  constructor(public accountId: number, public name: string, public value: string) {
  }
}

export class AccountProfileData {
  [key: string]: string;
}

export class AccountPreferredVendorItem {
  constructor(public accountId: number, public name: string, public value: string) {
  }
}

export class AccountPreferredVendorData {
  [key: string]: string;
}

export class AccountAssetItem {
  constructor(public accountId: number, public name: string, public value: number) {
  }
}

export class AccountAssetData {
  [key: string]: number;
}

export class User extends BaseModel {
  cognitoAttributes? = ['email'];

  public authenticationType?: string;
  public email?: string;
  public password?: string;
  public firstName?: string;
  public lastName?: string;
  public accountId?: number;
  public accountName?: string;
  public createdAt?: Date;
  public updatedAt?: Date;
  public roles?: Role[];
  public credentials?: UserCredential[];
  public preferences?: UserPreference[];

  constructor(obj: any = {} as User) {
    super(obj.id);
    this.authenticationType = obj.authenticationType;
    this.email = obj.email;
    this.password = obj.password;
    this.firstName = obj.firstName;
    this.lastName = obj.lastName;
    this.accountId = obj.accountId;
    this.accountName = obj.accountName;
    this.roles = obj.roles;
    this.createdAt = obj.createdAt;
    this.updatedAt = obj.updatedAt;
    this.credentials = obj.credentials;
    this.preferences = obj.preferences;

    if (obj.credentials) {
      this.credentials = obj.credentials.map((el) => new UserCredential(el));
    }

    if (obj.preferences) {
      this.preferences = obj.preferences.map((el) => new UserPreference(el));
    }
  }

  getPreference(preferenceKey): UserPreference {
    return this.preferences.find(el => el.key === preferenceKey);
  }

  get directEnabled():boolean {
    const cred = this.credentials ? this.credentials.find((el) => el.issuer === environment.cognitoJwtIssuer) : undefined;

    return (cred !== undefined);
  }

  get manageEnabled():boolean {
    const cred = this.credentials ? this.credentials.find((el) => el.issuer === 'CONNECTWISE') : undefined;
    return (cred !== undefined);
  }

  get ssoEnabled():boolean {
    const cred = this.credentials ? this.credentials.find((el) => el.issuer === environment.cwJwtIssuer) : undefined;
    return (cred !== undefined);
  }
}

export class ApiKey extends BaseModel {
  public publicKey?: string;
  public privateKeyHash?: string;
  public isActive?: boolean;
  public description?: string;
  public createdAt?: Date;

  constructor(obj: any = {} as ApiKey) {
    super(obj.id);
    this.publicKey = obj.publicKey;
    this.privateKeyHash = obj.privateKeyHash;
    this.isActive = obj.isActive;
    this.description = obj.description;
    this.createdAt = obj.createdAt;
  }
}

export class UserCredential extends BaseModel {
  public userId?: string;
  public issuer?: string;
  public externalId?: string;
  public createdAt?: Date;
  public mfaEnabled?: boolean;

  constructor(obj: any = {} as UserCredential) {
    super(obj.id);
    this.userId = obj.userId;
    this.issuer = obj.issuer;
    this.externalId = obj.externalId;
    this.createdAt = obj.createdAt;
    this.mfaEnabled = obj.mfaEnabled;
  }
}

export class UserPreference extends BaseModel {
  public userId?: string;
  public preferenceId?: string;
  public key?: string;
  public type?: string;
  public description?: string;
  public value?: string;
  public defaultValue?: string;
  public appliedValue?: string;

  constructor(obj: any = {} as UserPreference) {
    super(obj.id);
    this.userId = obj.userId;
    this.preferenceId = obj.preferenceId;
    this.key = obj.key;
    this.type = obj.type;
    this.description = obj.description;
    this.value = obj.value;
    this.defaultValue = obj.defaultValue;
    this.appliedValue = obj.appliedValue;
  }

  getBooleanValue():boolean {
    if (this.type === 'boolean') {
      return this.appliedValue.toLowerCase() === 'true';
    } else {
      return undefined;
    }
  }
}

export class Role extends BaseModel {
  public name?: string;

  constructor(obj: any = {} as Role) {
    super(obj.id);
    this.name = obj.name;
  }
}

export class Plan extends BaseModel {
  public name?: string;
  public description?: string;
  public trialPeriodDays?: number;
  public isPublic?: boolean;
  public createdAt?: Date;
  public updatedAt?: Date;
  public isActive: boolean;
  public externalProduct: string;
  public subscriptions: number;
  public features?: {id?:number, featureType?:string, planId?:number, subscriptionId?:number, value?:any, trialValue?:any}[];

  constructor(obj: any = {} as Plan) {
    super(obj.id);
    this.name = obj.name;
    this.description = obj.description;
    this.trialPeriodDays = obj.trialPeriodDays;
    this.isPublic = obj.isPublic;
    this.features = obj.features;
    this.createdAt = obj.createdAt;
    this.updatedAt = obj.updatedAt;
    this.isActive = (obj.isActive && obj.isActive > 0) ? true : false;
    this.externalProduct = obj.externalProduct;
    this.subscriptions = obj.subscriptions;

    if (obj.features) {
      this.features = obj.features.map((el) => {
        const featureType = AccountFeatureTypes.find((fel) => {
          return fel.featureType === el.featureType;
        });

        let val:any = el.value;
        let trialVal:any = el.trialValue;

        switch (featureType.dataType) {
          case 'number':
            val = (val !== undefined && val !== null) ? +val : undefined;
            trialVal = (trialVal !== undefined && trialVal !== null) ? +trialVal : undefined;
            break;
          case 'boolean':
            val = (val && val>0) ? true : false;
            trialVal = (trialVal && trialVal>0) ? true : false;
            break;
          default:
            val = val;
            trialVal = trialVal;
        }
        return {id:el.id, featureType:el.featureType, planId:el.planId, subscriptionId:el.subscriptionId, value:val, trialValue:trialVal};
      })
    } else {
      this.features = []
    }
  }

  getFeature(featureType:string, trial:boolean = false) {
    const f = this.features.find(el => el.featureType === featureType);
    if (f) {
      return (trial) ? f.trialValue : f.value;
    } else {
      return undefined;
    }
  }
}

export class Brand {
  public id: number;
  public name: string;
  public baseColor: string;
  public highlightColor: string;
  public textBackgroundColor: string;
  public lowScoreColor: string;
  public mediumScoreColor: string;
  public highScoreColor: string;
  public criticalScoreColor: string;
  public documentTitlePage: string;
  public documentPageHeader: string;
  public nistSubSectionTable: string;
  public isActive: boolean;
  public createdAt: Date;
  public updatedAt: Date;

  constructor(obj: any = {} as Brand) {
    if (!obj) {
      return;
    }
    this.id = obj.id;
    this.name = obj.name;
    this.baseColor = obj.baseColor;
    this.highlightColor = obj.highlightColor;
    this.textBackgroundColor = obj.textBackgroundColor;
    this.lowScoreColor = obj.lowScoreColor;
    this.mediumScoreColor = obj.mediumScoreColor;
    this.highScoreColor = obj.highScoreColor;
    this.criticalScoreColor = obj.criticalScoreColor;
    this.documentTitlePage = obj.documentTitlePage;
    this.documentPageHeader = obj.documentPageHeader;
    this.nistSubSectionTable = obj.nistSubSectionTable;
    this.isActive = obj.isActive;
    this.createdAt = obj.createdAt;
    this.updatedAt = obj.updatedAt;
  }
}

export class PlanSubscription extends BaseModel {
  public name?: string;
  public price?: number;
  public isActive?: boolean;
  public trialPeriodEndDate?: Date;
  public billingStartDate?: Date;
  public paymentStatus?: string;
  public planId?: number;
  public plan?: Plan;
  public createdAt?: Date;
  public updatedAt?: Date;
  public cancelledAt?: Date;
  public features?: {id?:number, featureType?:string, planId?:number, subscriptionId?:number, value?:any, trialValue?:any}[];

  constructor(obj: any = {} as PlanSubscription) {
    super(obj.id);
    this.name = obj.name;
    this.price = obj.price;
    this.isActive = obj.isActive;
    this.trialPeriodEndDate = obj.trialPeriodEndDate;
    this.paymentStatus = obj.paymentStatus;
    this.billingStartDate = obj.billingStartDate;
    this.planId = obj.planId;
    this.plan = new Plan(obj.plan);
    this.features = obj.features;
    this.createdAt = obj.createdAt;
    this.updatedAt = obj.updatedAt
    this.cancelledAt = obj.cancelledAt;

    this.features = this.features.map((el) => {
      const featureType = AccountFeatureTypes.find((fel) => {
        return fel.featureType === el.featureType;
      });

      let val:any = el.value;
      let trialVal:any = el.trialValue;

      switch (featureType.dataType) {
        case 'number':
          val = (val !== undefined && val !== null) ? +val : undefined;
          trialVal = (trialVal !== undefined && trialVal !== null) ? +trialVal : undefined;
          break;
        case 'boolean':
          val = (val && val>0) ? true : false;
          trialVal = (trialVal && trialVal>0) ? true : false;
          break;
        default:
          val = val;
          trialVal = trialVal;
      }
      return {id:el.id, featureType:el.featureType, planId:el.planId, subscriptionId:el.subscriptionId, value:val, trialValue:trialVal};
  });
  }

  isInTrial():boolean {
    if (this.trialPeriodEndDate && this.paymentStatus === 'trial') {
      return moment(this.trialPeriodEndDate).isAfter(moment());
    } else {
      return false;
    }
  }

  getFeature(feature:string):any {
    if (this.features) {
      const acctFeature = this.features.find(el => el.featureType === feature);
      if (acctFeature) {
        let valCheck = acctFeature.value;
        if (this.isInTrial()) {
          valCheck = acctFeature.trialValue;
        }
        return valCheck
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }

}
